import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { apiConnectorGet, apiConnectorPost } from "../../services/apiconnector";
import { endpoint } from "../../services/urls";
import { parse } from "postcss";

const PLAN_STYLES = [
  {
    riskIcon: "✅",
    accent: "#00e5ff",
    glow: "rgba(0,229,255,0.18)",
    gradient:
      "linear-gradient(135deg,rgba(0,229,255,0.10) 0%,rgba(0,180,200,0.04) 100%)",
    border: "rgba(0,229,255,0.28)",
  },
  {
    riskIcon: "⚖️",
    accent: "#4ade80",
    glow: "rgba(74,222,128,0.18)",
    gradient:
      "linear-gradient(135deg,rgba(74,222,128,0.10) 0%,rgba(22,163,74,0.04) 100%)",
    border: "rgba(74,222,128,0.28)",
  },
  {
    riskIcon: "📈",
    accent: "#facc15",
    glow: "rgba(250,204,21,0.18)",
    gradient:
      "linear-gradient(135deg,rgba(250,204,21,0.10) 0%,rgba(202,138,4,0.04) 100%)",
    border: "rgba(250,204,21,0.28)",
  },
  {
    riskIcon: "🔥",
    accent: "#f87171",
    glow: "rgba(248,113,113,0.18)",
    gradient:
      "linear-gradient(135deg,rgba(248,113,113,0.10) 0%,rgba(220,38,38,0.04) 100%)",
    border: "rgba(248,113,113,0.28)",
  },
];

export default function Bot() {
  const [view, setView] = useState("plans");
  const [selected, setSelected] = useState(null);
  const [amount, setAmount] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [topupWallet, setTopupWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [custId, setCustId] = useState("");
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  // null = checking, 0 = subscription required, 1 = already subscribed
  const [botStatus, setBotStatus] = useState(null);
  const [subSubmitting, setSubSubmitting] = useState(false);

  const fetchProfile = () =>
    apiConnectorGet(endpoint.member_details)
      .then((res) => {
        const profile = res?.data?.result?.[0];
        setTopupWallet(parseFloat(profile?.tr03_fund_wallet ?? 0));
        setCustId(profile?.lgn_cust_id || "");
      })
      .catch(() => toast.error("Failed to load wallet balance"))
      .finally(() => setWalletLoading(false));

  const checkBotSubscription = () =>
    apiConnectorGet(endpoint.check_bot_subscription)
      .then((res) => {
        const status = res?.data?.response?.[0]?.status;
        setBotStatus(status === 1 ? 1 : 0);
      })
      .catch(() => setBotStatus(0));

  useEffect(() => {
    fetchProfile();
    checkBotSubscription();

    apiConnectorGet(endpoint.get_package_details)
      .then((res) => {
        const raw = res?.data?.result?.data || [];
        const mapped = [...raw].reverse().map((p, i) => ({
          id: p.m02_pkg_id,
          name: p.m02_pkg_name,
          description: p.m02_description,
          range: `${parseFloat(p.m02_roi_pro_per)}% – ${parseFloat(p.m02_roi_pro_per2)}%`,
          period: "Monthly",
          ...PLAN_STYLES[i % PLAN_STYLES.length],
        }));
        setPlans(mapped);
      })
      .catch(() => toast.error("Failed to load bot plans"))
      .finally(() => setPlansLoading(false));
  }, []);

  const openSheet = (plan) => {
    setSelected(plan);
    setAmount("");
  };

  const closeSheet = () => {
    setSelected(null);
    setAmount("");
  };

  const fixedAmount = parseFloat(amount) || 0;

  const handleConfirm = async () => {
    if (!fixedAmount || fixedAmount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (fixedAmount < 100) {
      toast.error("Minimum deposit is $100");
      return;
    }
    if (topupWallet !== null && fixedAmount > topupWallet) {
      toast.error("Insufficient balance");
      return;
    }
    setConfirming(true);
    const res = await apiConnectorPost(endpoint.member_fund_transfer, {
      wallet_type: "auto_bot_trade_wallet",
      pkg_amount: fixedAmount.toFixed(2),
      pkg_id: selected.id,
      to_cust_id: custId,
    });
    setConfirming(false);
    if (res?.data?.success) {
      closeSheet();
      fetchProfile();
      toast.success(
        res.data.message ||
          `Bot activated — $${fixedAmount.toFixed(2)} allocated`,
        { duration: 3500 },
      );
    } else {
      toast.error(
        res?.data?.message || "Failed to activate bot. Please try again.",
      );
    }
  };

  const handleSubscribe = async () => {
    setSubSubmitting(true);
    try {
      const res = await apiConnectorPost(endpoint.member_fund_transfer, {
        wallet_type: "bot_subscriptoin",
        pkg_amount: (25).toFixed(2),
        pkg_id: 1,
        to_cust_id: custId,
      });
      if (res?.data?.success) {
        toast.success(res.data.message || "Subscription activated!");
        setBotStatus(1);
      } else {
        toast.error(
          res?.data?.message || "Subscription failed. Please try again.",
        );
      }
    } catch {
      toast.error("Something went wrong.");
    } finally {
      setSubSubmitting(false);
    }
  };

  return (
    <div style={s.root}>
      <div style={s.grid} />
      <div style={s.glowTL} />
      <div style={s.glowBR} />

      <div style={s.container}>
        {/* Header */}
        <div style={s.pageHeader}>
          <span style={s.headerIcon}>🤖</span>
          <div style={{ flex: 1 }}>
            <h1 style={s.pageTitle}>Trading Bots</h1>
            <p style={s.pageSubtitle}>Select a plan to auto-trade</p>
          </div>
          <button
            style={{
              ...s.historyBtn,
              ...(view === "history" ? s.historyBtnActive : {}),
              ...(botStatus !== 1
                ? { opacity: 0.4, cursor: "not-allowed" }
                : {}),
            }}
            disabled={botStatus !== 1}
            onClick={() =>
              setView((v) => (v === "history" ? "plans" : "history"))
            }
          >
            {view === "history" ? "← Plans" : "History"}
          </button>
        </div>

        {botStatus === null ? (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                border: "3px solid rgba(0,229,255,0.12)",
                borderTopColor: "#00e5ff",
                animation: "spin 0.8s linear infinite",
                margin: "0 auto",
              }}
            />
          </div>
        ) : botStatus === 0 ? null : view === "history" ? (
          <BotHistoryView plans={plans} />
        ) : (
          <>
            {/* Info banner */}
            <div style={s.infoBanner}>
              <span style={s.infoIcon}>💡</span>
              <p style={s.infoText}>
                Allocate a portion of your balance to a bot. The remaining
                balance stays available for manual trading.
              </p>
            </div>

            {/* Plan cards */}
            <div style={s.grid2} className="!grid !grid-cols-1 ">
              {plansLoading
                ? [0, 1, 2, 3].map((i) => (
                    <div key={i} style={s.cardSkeleton} />
                  ))
                : plans.map((plan) => (
                    <PlanCard
                      key={plan.id}
                      plan={plan}
                      onSelect={() => openSheet(plan)}
                    />
                  ))}
            </div>
          </>
        )}
      </div>

      {/* Subscription gate */}
      {botStatus === 0 && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1500,
            background: "rgba(0,0,0,0.65)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 380,
              maxHeight: "100%",
              overflowY: "auto",
              boxSizing: "border-box",
              background: "#0a1520",
              border: "1px solid rgba(0,229,255,0.18)",
              borderRadius: 20,
              padding: "26px 22px 22px",
              animation: "fadeUp 0.3s cubic-bezier(0.22,1,0.36,1) both",
            }}
          >
            <div style={{ textAlign: "center", marginBottom: 18 }}>
              <span style={{ fontSize: 34 }}>🔒</span>
              <h3
                style={{
                  color: "#e0f7fa",
                  fontSize: 18,
                  fontWeight: 700,
                  margin: "10px 0 6px",
                }}
              >
                Activate Bot Subscription
              </h3>
              <p
                style={{
                  fontSize: 12,
                  color: "rgba(100,180,200,0.6)",
                  margin: 0,
                  lineHeight: 1.6,
                }}
              >
                A one-time subscription fee unlocks access to all trading bots.
              </p>
            </div>

            <label style={s.inputLabel}>Subscription Fee</label>
            <div
              style={{
                ...s.inputWrap,
                borderColor: "rgba(0,229,255,0.25)",
                marginBottom: 18,
                boxSizing: "border-box",
              }}
            >
              <span
                style={{
                  ...s.inputPrefix,
                  color: "#00e5ff",
                  flexShrink: 0,
                }}
              >
                $
              </span>
              <input
                type="text"
                value="25.00"
                readOnly
                style={{
                  ...s.input,
                  minWidth: 0,
                  cursor: "not-allowed",
                  color: "rgba(224,247,250,0.7)",
                }}
              />
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "rgba(100,180,200,0.55)",
                  fontFamily: "'Space Mono',monospace",
                  letterSpacing: "0.08em",
                  flexShrink: 0,
                  marginLeft: 8,
                }}
              >
                USDT
              </span>
            </div>

            <ConfirmBtn
              label={subSubmitting ? "Processing…" : "Pay $25 & Activate"}
              color="#00e5ff"
              onClick={handleSubscribe}
              loading={subSubmitting}
              disabled={!custId}
            />
          </div>
        </div>
      )}

      {/* Bottom sheet overlay */}
      {selected && (
        <>
          <div style={s.backdrop} onClick={closeSheet} />
          <div style={s.sheet}>
            {/* Sheet handle */}
            <div style={s.sheetHandle} />

            {/* Plan summary inside sheet */}
            <div style={{ ...s.sheetAccentBar, background: selected.accent }} />
            <div style={s.sheetHeader}>
              <span style={{ fontSize: 28 }}>{selected.riskIcon}</span>
              <div>
                <p style={{ ...s.sheetRange, color: selected.accent }}>
                  {selected.range}
                  <span style={s.sheetPeriod}> / {selected.period}</span>
                </p>
                <p style={s.sheetRisk}>
                  {selected.name} · {selected.description}
                </p>
              </div>
            </div>

            {/* Amount input */}
            {/* Available balance */}
            <div style={s.balanceRow}>
              <span style={s.balanceLabel}>Available (Topup Wallet)</span>
              <span style={{ ...s.balanceValue, color: selected.accent }}>
                {walletLoading ? "…" : `$${topupWallet?.toFixed(2) ?? "0.00"}`}
              </span>
            </div>

            <label style={s.inputLabel}>Amount to invest</label>
            <div
              style={{
                ...s.inputWrap,
                borderColor: amount ? selected.accent : "rgba(255,255,255,0.1)",
              }}
            >
              <span
                style={{
                  ...s.inputPrefix,
                  color: selected.accent,
                  marginRight: 4,
                  marginLeft: 0,
                }}
              >
                $
              </span>
              <input
                type="number"
                inputMode="decimal"
                placeholder="0.00"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                style={s.input}
                autoFocus
              />
              <button
                style={{
                  ...s.maxBtn,
                  color: selected.accent,
                  borderColor: selected.accent,
                  marginLeft: 10,
                }}
                onClick={() =>
                  setAmount(String(topupWallet?.toFixed(2) || "0"))
                }
              >
                MAX
              </button>
            </div>

            {/* Notes */}
            <div
              style={{
                background: "rgba(250,204,21,0.05)",
                border: "1px solid rgba(250,204,21,0.18)",
                borderRadius: 10,
                padding: "10px 14px",
                margin: "12px 0 16px",
              }}
            >
              {/* <p
                style={{
                  fontSize: 11,
                  color: "rgba(250,204,21,0.85)",
                  margin: "0 0 4px",
                  lineHeight: 1.6,
                }}
              >
                ⚠️ $35 subscription charges will be auto deducted only at first
                time.
              </p> */}
              <p
                style={{
                  fontSize: 11,
                  color: "rgba(250,204,21,0.85)",
                  margin: 0,
                  lineHeight: 1.6,
                }}
              >
                💵 Minimum deposit is $100.
              </p>
            </div>

            {/* Confirm */}
            {(() => {
              const belowMin = fixedAmount > 0 && fixedAmount < 100;
              const exceeded =
                topupWallet !== null && fixedAmount > topupWallet;
              const invalid = belowMin || exceeded;
              const isDisabled = confirming || invalid;
              return (
                <button
                  style={{
                    ...s.confirmBtn,
                    background: invalid
                      ? "rgba(255,255,255,0.08)"
                      : selected.accent,
                    boxShadow: invalid ? "none" : `0 6px 28px ${selected.glow}`,
                    color: invalid ? "rgba(255,255,255,0.3)" : "#020c12",
                    border: invalid
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "none",
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    opacity: confirming ? 0.7 : 1,
                  }}
                  onClick={handleConfirm}
                  disabled={isDisabled}
                >
                  {confirming
                    ? "Activating…"
                    : exceeded
                      ? "Insufficient Balance"
                      : belowMin
                        ? "Minimum $100 Required"
                        : "Activate Bot"}
                </button>
              );
            })()}

            <button style={s.cancelBtn} onClick={closeSheet}>
              Cancel
            </button>
          </div>
        </>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Sora:wght@300;400;500;600;700&display=swap');
        @keyframes fadeUp { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
        @keyframes slideUp { from { transform:translateY(100%); } to { transform:translateY(0); } }
        @keyframes pulse { 0%,100% { opacity:0.4; } 50% { opacity:0.8; } }
        @keyframes spin { to { transform:rotate(360deg); } }
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance:none; margin:0; }
        input[type=number] { -moz-appearance: textfield; }
      `}</style>
    </div>
  );
}

function fmtDate(str) {
  if (!str) return "-";
  const d = new Date(str);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function BotHistoryView({ plans }) {
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [totalPage, setTotalPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  // action modal: { type: "sell" | "bookProfit" | "changePlan", row }
  const [actionModal, setActionModal] = useState(null);
  const [newPlanId, setNewPlanId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    apiConnectorPost(endpoint.get_bot_history, {
      search: "",
      start_date: "",
      end_date: "",
      page,
      count: 10,
    })
      .then((res) => {
        setRows(res?.data?.result?.data || []);
        setTotalPage(res?.data?.result?.totalPage || 1);
      })
      .catch(() => toast.error("Failed to load bot history"))
      .finally(() => setLoading(false));
  }, [page, refreshKey]);

  const planMap = Object.fromEntries((plans || []).map((p) => [p.id, p]));

  const closeModal = () => {
    setActionModal(null);
    setNewPlanId(null);
  };

  const afterAction = (msg) => {
    toast.success(msg);
    closeModal();
    setRefreshKey((k) => k + 1);
  };

  const handleSell = async () => {
    setActionLoading(true);
    try {
      const res = await apiConnectorPost(endpoint.member_fund_transfer, {
        pkg_amount:
          actionModal.row.tr09_pack_amount -
          actionModal.row.tr09_withdraw_amount,
        pkg_id: actionModal.row.tr09_id,
        to_cust_id: actionModal.row.tr03_cust_id,
        wallet_type: "transfer_auto_trade_principal_to_spot_wallet",
      });
      if (res?.data?.success)
        afterAction(
          res.data.message || "Bot sold — funds sent to spot wallet!",
        );
      else toast.error(res?.data?.message || "Failed to sell bot.");
    } catch {
      toast.error("Something went wrong.");
    }
    setActionLoading(false);
  };

  const handleBookProfit = async () => {
    setActionLoading(true);
    try {
      const res = await apiConnectorPost(endpoint.member_fund_transfer, {
        pkg_amount:
          actionModal.row.tr09_roi_amount - actionModal.row.tr09_withdraw_roi,
        pkg_id: actionModal.row.tr09_id,
        to_cust_id: actionModal.row.tr03_cust_id,
        wallet_type: "transfer_auto_trade_profit_to_spot_wallet",
      });
      if (res?.data?.success)
        afterAction(res.data.message || "Profit booked to spot wallet!");
      else toast.error(res?.data?.message || "Failed to book profit.");
    } catch {
      toast.error("Something went wrong.");
    }
    setActionLoading(false);
  };

  const handleChangePlan = async () => {
    if (!newPlanId) {
      toast.error("Please select a new plan.");
      return;
    }
    setActionLoading(true);
    try {
      const res = await apiConnectorPost(endpoint.change_bot_plan, {
        t_id: actionModal.row.tr09_id,
        new_bot_id: newPlanId,
      });
      if (res?.data?.success)
        afterAction(res.data.message || "Bot plan changed!");
      else toast.error(res?.data?.message || "Failed to change plan.");
    } catch {
      toast.error("Something went wrong.");
    }
    setActionLoading(false);
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "48px 0" }}>
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            border: "3px solid rgba(0,229,255,0.12)",
            borderTopColor: "#00e5ff",
            animation: "spin 0.8s linear infinite",
            margin: "0 auto",
          }}
        />
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div style={{ textAlign: "center", padding: "56px 0" }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>🤖</div>
        <p style={{ color: "rgba(100,180,200,0.45)", fontSize: 13 }}>
          No bot history yet
        </p>
      </div>
    );
  }

  const row = actionModal?.row;
  const rowAccent = row
    ? planMap[row.tr09_pack_id]?.accent || "#00e5ff"
    : "#00e5ff";

  return (
    <div>
      {rows.map((row) => {
        const plan = planMap[row.tr09_pack_id];
        const accent = plan?.accent || "#00e5ff";
        const roi = parseFloat(row.tr09_roi_amount ?? 0);
        const roiWithdrawable =
          parseFloat(row.tr09_roi_amount ?? 0) -
          parseFloat(row.tr09_withdraw_roi ?? 0);

        return (
          <div
            key={row.tr09_id}
            style={{
              background: "rgba(255,255,255,0.03)",
              border: `1px solid ${accent}22`,
              borderRadius: 14,
              padding: "14px 16px",
              marginBottom: 10,
            }}
          >
            {/* Row 1: plan badge + invested amount */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  fontFamily: "'Space Mono',monospace",
                  color: accent,
                  background: `${accent}15`,
                  border: `1px solid ${accent}33`,
                  borderRadius: 6,
                  padding: "3px 8px",
                }}
              >
                {plan?.name || `Plan #${row.tr09_pack_id}`}
              </span>
              <span
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  fontFamily: "'Space Mono',monospace",
                  color: accent,
                }}
              >
                $
                {parseFloat(
                  row.tr09_pack_amount - row.tr09_withdraw_amount,
                ).toFixed(2)}
              </span>
            </div>

            {/* Row 2: ROI earned */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <span style={{ fontSize: 11, color: "rgba(100,180,200,0.55)" }}>
                Total ROI earned&nbsp;
                <span style={{ color: "#4ade80", fontWeight: 700 }}>
                  ${roi.toFixed(2)}
                </span>
                &nbsp;·&nbsp;
                {Number(Number(row.tr09_roi_comp_days || 0) / 4)?.toFixed(
                  0,
                )}{" "}
                day
                {row.tr09_roi_comp_days !== 1 ? "s" : ""}
              </span>
              <span style={{ fontSize: 11, color: "rgba(100,180,200,0.55)" }}>
                Withdrawable ROI&nbsp;
                <span style={{ color: "#4ade80", fontWeight: 700 }}>
                  ${roiWithdrawable.toFixed(2)}
                </span>
              </span>
            </div>

            {/* Row 3: Tx ID + date */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <span
                style={{
                  fontSize: 9,
                  color: "rgba(100,180,200,0.35)",
                  fontFamily: "'Space Mono',monospace",
                }}
              >
                #{row.tr09_trans_id}
              </span>
              <span style={{ fontSize: 10, color: "rgba(100,180,200,0.45)" }}>
                {fmtDate(row.tr09_created_at)}
              </span>
            </div>

            {/* Action buttons */}
            <div
              style={{
                display: "flex",
                gap: 6,
                borderTop: "1px solid rgba(255,255,255,0.05)",
                paddingTop: 10,
              }}
            >
              {/* Sell */}
              <button
                onClick={() => setActionModal({ type: "sell", row })}
                style={actionBtn("#f87171")}
              >
                🔴 Sell
              </button>
              {/* Book Profit */}
              <button
                onClick={() => setActionModal({ type: "bookProfit", row })}
                disabled={roi <= 0}
                style={{
                  ...actionBtn("#4ade80"),
                  opacity: roi <= 0 ? 0.35 : 1,
                  cursor: roi <= 0 ? "not-allowed" : "pointer",
                }}
              >
                💰 Book Profit
              </button>
              {/* Change Plan */}
              <button
                onClick={() => {
                  setNewPlanId(null);
                  setActionModal({ type: "changePlan", row });
                }}
                style={actionBtn("#00e5ff")}
              >
                🔄 Change
              </button>
            </div>
          </div>
        );
      })}

      {/* Pagination */}
      {totalPage > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 12,
            marginTop: 16,
          }}
        >
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              background: "rgba(0,229,255,0.06)",
              border: "1px solid rgba(0,229,255,0.2)",
              borderRadius: 8,
              color: page === 1 ? "rgba(100,180,200,0.2)" : "#00e5ff",
              fontSize: 13,
              padding: "7px 18px",
              cursor: page === 1 ? "not-allowed" : "pointer",
            }}
          >
            ‹ Prev
          </button>
          <span
            style={{
              fontSize: 11,
              color: "rgba(100,180,200,0.5)",
              fontFamily: "'Space Mono',monospace",
            }}
          >
            {page} / {totalPage}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPage, p + 1))}
            disabled={page === totalPage}
            style={{
              background: "rgba(0,229,255,0.06)",
              border: "1px solid rgba(0,229,255,0.2)",
              borderRadius: 8,
              color: page === totalPage ? "rgba(100,180,200,0.2)" : "#00e5ff",
              fontSize: 13,
              padding: "7px 18px",
              cursor: page === totalPage ? "not-allowed" : "pointer",
            }}
          >
            Next ›
          </button>
        </div>
      )}

      {/* ── Action Modal ── */}
      {actionModal && row && (
        <>
          {/* Backdrop */}
          <div
            onClick={closeModal}
            style={{
              // position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.72)",
              backdropFilter: "blur(4px)",
              zIndex: 100,
            }}
          />

          {/* Sheet */}
          <div
            style={{
              // position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              maxWidth: 480,
              margin: "0 auto",
              background: "#0a1520",
              border: "1px solid rgba(0,229,255,0.15)",
              borderRadius: "24px 24px 0 0",
              padding: "8px 20px 44px",
              zIndex: 101,
              animation: "slideUp 0.3s cubic-bezier(0.22,1,0.36,1) both",
            }}
          >
            {/* Handle */}
            <div
              style={{
                width: 40,
                height: 4,
                borderRadius: 2,
                background: "rgba(255,255,255,0.15)",
                margin: "8px auto 18px",
              }}
            />

            {/* Accent bar */}
            <div
              style={{
                height: 3,
                borderRadius: 2,
                marginBottom: 18,
                opacity: 0.7,
                background: rowAccent,
              }}
            />

            {/* SELL */}
            {actionModal.type === "sell" && (
              <>
                <p
                  style={{
                    fontSize: 11,
                    color: "rgba(100,180,200,0.5)",
                    textTransform: "uppercase",
                    letterSpacing: "0.12em",
                    fontFamily: "'Space Mono',monospace",
                    margin: "0 0 6px",
                  }}
                >
                  Sell Bot
                </p>
                <h3
                  style={{
                    color: "#e0f7fa",
                    fontSize: 18,
                    fontWeight: 700,
                    margin: "0 0 16px",
                  }}
                >
                  Close this position?
                </h3>
                <InfoRow
                  label="Base Capital"
                  value={`$${parseFloat(row.tr09_pack_amount - row.tr09_withdraw_amount).toFixed(2)}`}
                  accent={rowAccent}
                />
                <InfoRow
                  label="ROI Earned (stays)"
                  value={`$${parseFloat(row.tr09_roi_amount - row.tr09_withdraw_roi).toFixed(2)}`}
                  accent="#4ade80"
                />
                <div
                  style={{
                    background: "rgba(248,113,113,0.06)",
                    border: "1px solid rgba(248,113,113,0.2)",
                    borderRadius: 10,
                    padding: "10px 14px",
                    margin: "14px 0 20px",
                  }}
                >
                  <p
                    style={{
                      fontSize: 11,
                      color: "rgba(248,113,113,0.8)",
                      margin: 0,
                      lineHeight: 1.6,
                    }}
                  >
                    ⚠️ Only your base capital of $
                    {parseFloat(
                      row.tr09_pack_amount - row.tr09_withdraw_amount,
                    ).toFixed(2)}{" "}
                    will be transferred to your spot wallet. Earned ROI is not
                    included.
                  </p>
                  <p
                    style={{
                      fontSize: 11,
                      color: "rgba(248,113,113,0.8)",
                      margin: 0,
                      lineHeight: 1.6,
                    }}
                  >
                    ✅ You can withdrawal your capital after 90 days.
                  </p>
                </div>
                <ConfirmBtn
                  label={actionLoading ? "Processing…" : "Confirm Sell"}
                  color="#f87171"
                  onClick={handleSell}
                  loading={actionLoading}
                />
                <CancelBtn onClick={closeModal} />
              </>
            )}

            {/* BOOK PROFIT */}
            {actionModal.type === "bookProfit" && (
              <>
                <p
                  style={{
                    fontSize: 11,
                    color: "rgba(100,180,200,0.5)",
                    textTransform: "uppercase",
                    letterSpacing: "0.12em",
                    fontFamily: "'Space Mono',monospace",
                    margin: "0 0 6px",
                  }}
                >
                  Book Profit
                </p>
                <h3
                  style={{
                    color: "#e0f7fa",
                    fontSize: 18,
                    fontWeight: 700,
                    margin: "0 0 16px",
                  }}
                >
                  Withdraw earned ROI?
                </h3>
                <InfoRow
                  label="ROI to Book"
                  value={`$${parseFloat(row.tr09_roi_amount - row.tr09_withdraw_roi).toFixed(2)}`}
                  accent="#4ade80"
                />
                <InfoRow
                  label="Capital Stays"
                  value={`$${parseFloat(row.tr09_pack_amount - row.tr09_withdraw_amount).toFixed(2)}`}
                  accent={rowAccent}
                />
                <div
                  style={{
                    background: "rgba(74,222,128,0.05)",
                    border: "1px solid rgba(74,222,128,0.18)",
                    borderRadius: 10,
                    padding: "10px 14px",
                    margin: "14px 0 20px",
                  }}
                >
                  <p
                    style={{
                      fontSize: 11,
                      color: "rgba(74,222,128,0.8)",
                      margin: 0,
                      lineHeight: 1.6,
                    }}
                  >
                    ✅ Only your earned ROI is sent to spot wallet. Base capital
                    stays in the bot and continues trading.
                  </p>
                </div>
                <ConfirmBtn
                  label={actionLoading ? "Processing…" : "Book Profit"}
                  color="#4ade80"
                  onClick={handleBookProfit}
                  loading={actionLoading}
                />
                <CancelBtn onClick={closeModal} />
              </>
            )}

            {/* CHANGE PLAN */}
            {actionModal.type === "changePlan" && (
              <>
                <p
                  style={{
                    fontSize: 11,
                    color: "rgba(100,180,200,0.5)",
                    textTransform: "uppercase",
                    letterSpacing: "0.12em",
                    fontFamily: "'Space Mono',monospace",
                    margin: "0 0 6px",
                  }}
                >
                  Change Plan
                </p>
                <h3
                  style={{
                    color: "#e0f7fa",
                    fontSize: 18,
                    fontWeight: 700,
                    margin: "0 0 4px",
                  }}
                >
                  Switch bot plan
                </h3>
                <p
                  style={{
                    fontSize: 11,
                    color: "rgba(100,180,200,0.5)",
                    margin: "0 0 16px",
                  }}
                >
                  Current:{" "}
                  <span style={{ color: rowAccent, fontWeight: 600 }}>
                    {planMap[row.tr09_pack_id]?.name ||
                      `Plan #${row.tr09_pack_id}`}
                  </span>
                </p>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    marginBottom: 20,
                    maxHeight: 220,
                    overflowY: "auto",
                  }}
                >
                  {plans
                    .filter((p) => p.id !== row.tr09_pack_id)
                    .map((plan) => (
                      <button
                        key={plan.id}
                        onClick={() => setNewPlanId(plan.id)}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "12px 14px",
                          borderRadius: 12,
                          cursor: "pointer",
                          border:
                            newPlanId === plan.id
                              ? `1px solid ${plan.accent}`
                              : "1px solid rgba(255,255,255,0.07)",
                          background:
                            newPlanId === plan.id
                              ? `${plan.accent}12`
                              : "rgba(255,255,255,0.03)",
                          transition: "all 0.15s",
                        }}
                      >
                        <div style={{ textAlign: "left" }}>
                          <p
                            style={{
                              margin: 0,
                              fontSize: 12,
                              fontWeight: 700,
                              color: plan.accent,
                            }}
                          >
                            {plan.name}
                          </p>
                          <p
                            style={{
                              margin: "2px 0 0",
                              fontSize: 10,
                              color: "rgba(100,180,200,0.5)",
                            }}
                          >
                            {plan.range} / {plan.period}
                          </p>
                        </div>
                        <div
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            border: `2px solid ${newPlanId === plan.id ? plan.accent : "rgba(255,255,255,0.15)"}`,
                            background:
                              newPlanId === plan.id
                                ? plan.accent
                                : "transparent",
                            flexShrink: 0,
                          }}
                        />
                      </button>
                    ))}
                </div>
                <ConfirmBtn
                  label={actionLoading ? "Processing…" : "Confirm Change"}
                  color="#00e5ff"
                  onClick={handleChangePlan}
                  loading={actionLoading}
                  disabled={!newPlanId}
                />
                <CancelBtn onClick={closeModal} />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function InfoRow({ label, value, accent }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: 10,
        padding: "10px 14px",
        marginBottom: 8,
      }}
    >
      <span
        style={{
          fontSize: 10,
          color: "rgba(100,180,200,0.55)",
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          fontFamily: "'Space Mono',monospace",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 15,
          fontWeight: 700,
          fontFamily: "'Space Mono',monospace",
          color: accent,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function ConfirmBtn({ label, color, onClick, loading, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={loading || disabled}
      style={{
        width: "100%",
        height: 50,
        borderRadius: 14,
        border: "none",
        background: loading || disabled ? "rgba(255,255,255,0.06)" : color,
        color: loading || disabled ? "rgba(255,255,255,0.25)" : "#020c12",
        fontSize: 13,
        fontWeight: 700,
        fontFamily: "'Space Mono',monospace",
        letterSpacing: "0.08em",
        cursor: loading || disabled ? "not-allowed" : "pointer",
        marginBottom: 10,
        transition: "opacity 0.2s",
      }}
    >
      {label}
    </button>
  );
}

function CancelBtn({ onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        height: 44,
        borderRadius: 14,
        background: "transparent",
        border: "1px solid rgba(255,255,255,0.08)",
        color: "rgba(100,180,200,0.5)",
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
        fontFamily: "'Sora',sans-serif",
      }}
    >
      Cancel
    </button>
  );
}

function actionBtn(color) {
  return {
    flex: 1,
    padding: "7px 4px",
    borderRadius: 9,
    background: `${color}10`,
    border: `1px solid ${color}28`,
    color: color,
    fontSize: 10,
    fontWeight: 700,
    fontFamily: "'Space Mono',monospace",
    letterSpacing: "0.06em",
    cursor: "pointer",
    transition: "background 0.15s",
  };
}

function PlanCard({ plan, onSelect }) {
  return (
    <button
      onClick={onSelect}
      style={{
        ...s.card,
        background: plan.gradient,
        border: `1px solid ${plan.border}`,
        boxShadow: `0 4px 24px ${plan.glow}`,
      }}
    >
      {/* Top badge */}
      <div
        style={{
          ...s.cardBadge,
          color: plan.accent,
          borderColor: plan.border,
          background: `${plan.glow}`,
        }}
      >
        {plan.riskIcon} {plan.name}
      </div>

      {/* Return range */}
      <p style={{ ...s.cardRange, color: plan.accent }}>{plan.range}</p>
      <p style={s.cardPeriod}>{plan.period}</p>

      {/* Divider */}
      <div style={{ ...s.cardDivider, background: plan.border }} />

      {/* Description */}
      <p style={s.cardTagline}>✔️ {plan.description}</p>

      {/* CTA */}
      <div style={{ ...s.cardCta, color: plan.accent }}>Select Plan ›</div>
    </button>
  );
}

const s = {
  root: {
    minHeight: "100vh",
    background: "#020c12",
    fontFamily: "'Sora',sans-serif",
    position: "relative",
    overflow: "hidden",
    paddingBottom: 100,
  },
  grid: {
    position: "fixed",
    inset: 0,
    pointerEvents: "none",
    backgroundImage:
      "linear-gradient(rgba(0,229,255,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(0,229,255,0.03) 1px,transparent 1px)",
    backgroundSize: "44px 44px",
  },
  glowTL: {
    position: "fixed",
    top: -150,
    left: -100,
    width: 480,
    height: 480,
    borderRadius: "50%",
    pointerEvents: "none",
    background:
      "radial-gradient(circle,rgba(0,229,255,0.07) 0%,transparent 70%)",
  },
  glowBR: {
    position: "fixed",
    bottom: -100,
    right: -80,
    width: 380,
    height: 380,
    borderRadius: "50%",
    pointerEvents: "none",
    background:
      "radial-gradient(circle,rgba(0,180,200,0.05) 0%,transparent 70%)",
  },
  container: {
    maxWidth: 480,
    margin: "0 auto",
    padding: "24px 16px 40px",
    position: "relative",
    zIndex: 10,
    animation: "fadeUp 0.45s cubic-bezier(0.22,1,0.36,1) both",
  },

  // header
  pageHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 16,
  },
  headerIcon: { fontSize: 30 },
  pageTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: "#e0f7fa",
    letterSpacing: "-0.02em",
    margin: 0,
  },
  pageSubtitle: {
    fontSize: 11,
    color: "rgba(100,180,200,0.55)",
    margin: "2px 0 0",
    letterSpacing: "0.04em",
  },

  // info banner
  infoBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    background: "rgba(0,229,255,0.04)",
    border: "1px solid rgba(0,229,255,0.12)",
    borderRadius: 12,
    padding: "12px 14px",
    marginBottom: 20,
  },
  infoIcon: { fontSize: 14, flexShrink: 0, marginTop: 1 },
  infoText: {
    fontSize: 11,
    color: "rgba(100,180,200,0.7)",
    lineHeight: 1.6,
    margin: 0,
  },

  // 2-column grid
  grid2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  },
  cardSkeleton: {
    borderRadius: 18,
    height: 160,
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.06)",
    animation: "pulse 1.4s ease-in-out infinite",
  },

  // plan card
  card: {
    borderRadius: 18,
    padding: "16px 14px",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 6,
    cursor: "pointer",
    width: "100%",
    textAlign: "left",
    backdropFilter: "blur(20px)",
    transition: "transform 0.15s, box-shadow 0.15s",
  },
  cardBadge: {
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    padding: "3px 8px",
    borderRadius: 6,
    border: "1px solid",
    fontFamily: "'Space Mono',monospace",
  },
  cardRange: {
    fontSize: 20,
    fontWeight: 800,
    letterSpacing: "-0.03em",
    fontFamily: "'Space Mono',monospace",
    margin: "4px 0 0",
    lineHeight: 1,
  },
  cardPeriod: {
    fontSize: 10,
    color: "rgba(100,180,200,0.5)",
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    margin: 0,
  },
  cardDivider: {
    height: 1,
    width: "100%",
    opacity: 0.5,
    margin: "4px 0",
  },
  cardTagline: {
    fontSize: 11,
    color: "#e0f7fa",
    lineHeight: 1.5,
    margin: 0,
  },
  cardCta: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    marginTop: 6,
    fontFamily: "'Space Mono',monospace",
  },

  // backdrop
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.65)",
    backdropFilter: "blur(4px)",
    zIndex: 50,
  },

  // bottom sheet
  sheet: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    background: "#0a1520",
    border: "1px solid rgba(0,229,255,0.12)",
    borderRadius: "24px 24px 0 0",
    padding: "8px 20px 40px",
    zIndex: 51,
    maxWidth: 480,
    margin: "0 auto",
    animation: "slideUp 0.3s cubic-bezier(0.22,1,0.36,1) both",
  },
  sheetHandle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    background: "rgba(255,255,255,0.15)",
    margin: "8px auto 16px",
  },
  sheetAccentBar: {
    height: 3,
    borderRadius: 2,
    marginBottom: 16,
    opacity: 0.7,
  },
  sheetHeader: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    marginBottom: 20,
  },
  sheetRange: {
    fontSize: 22,
    fontWeight: 800,
    fontFamily: "'Space Mono',monospace",
    letterSpacing: "-0.02em",
    margin: 0,
  },
  sheetPeriod: {
    fontSize: 12,
    color: "rgba(100,180,200,0.55)",
    fontWeight: 400,
  },
  sheetRisk: {
    fontSize: 11,
    color: "rgba(100,180,200,0.6)",
    margin: "3px 0 0",
  },

  // balance row
  balanceRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: 10,
    padding: "10px 14px",
    marginBottom: 14,
  },
  balanceLabel: {
    fontSize: 10,
    color: "rgba(100,180,200,0.55)",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    fontFamily: "'Space Mono',monospace",
  },
  balanceValue: {
    fontSize: 15,
    fontWeight: 700,
    fontFamily: "'Space Mono',monospace",
  },

  // input
  inputLabel: {
    display: "block",
    fontSize: 10,
    color: "rgba(100,180,200,0.55)",
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    fontFamily: "'Space Mono',monospace",
    marginBottom: 8,
  },
  inputWrap: {
    display: "flex",
    alignItems: "center",
    background: "rgba(255,255,255,0.04)",
    border: "1px solid",
    borderRadius: 14,
    padding: "0 16px",
    height: 54,
    transition: "border-color 0.2s",
  },
  maxBtn: {
    background: "transparent",
    border: "1px solid",
    borderRadius: 6,
    padding: "3px 8px",
    fontSize: 9,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "'Space Mono',monospace",
    letterSpacing: "0.1em",
    flexShrink: 0,
  },
  inputPrefix: {
    fontSize: 20,
    fontWeight: 700,
    fontFamily: "'Space Mono',monospace",
    marginRight: 6,
    lineHeight: 1,
  },
  input: {
    flex: 1,
    background: "none",
    border: "none",
    outline: "none",
    color: "#e0f7fa",
    fontSize: 22,
    fontWeight: 700,
    fontFamily: "'Space Mono',monospace",
    letterSpacing: "-0.02em",
  },
  derivedRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 4px",
    marginTop: 8,
  },
  derivedLabel: {
    fontSize: 10,
    color: "rgba(100,180,200,0.45)",
    fontFamily: "'Space Mono',monospace",
    letterSpacing: "0.08em",
  },
  derivedValue: {
    fontSize: 15,
    fontWeight: 700,
    fontFamily: "'Space Mono',monospace",
  },
  inputHint: {
    fontSize: 10,
    color: "rgba(100,180,200,0.45)",
    margin: "8px 0 14px",
    lineHeight: 1.5,
  },

  // quick buttons
  quickRow: {
    display: "grid",
    gridTemplateColumns: "repeat(4,1fr)",
    gap: 8,
    marginBottom: 20,
  },
  quickBtn: {
    height: 36,
    borderRadius: 10,
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "rgba(224,247,250,0.7)",
    fontSize: 11,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'Space Mono',monospace",
    transition: "background 0.15s",
  },

  // confirm / cancel
  confirmBtn: {
    width: "100%",
    height: 52,
    borderRadius: 14,
    border: "none",
    color: "#020c12",
    fontSize: 13,
    fontWeight: 700,
    fontFamily: "'Space Mono',monospace",
    letterSpacing: "0.1em",
    cursor: "pointer",
    marginBottom: 10,
    transition: "opacity 0.2s",
  },
  cancelBtn: {
    width: "100%",
    height: 44,
    borderRadius: 14,
    background: "transparent",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "rgba(100,180,200,0.5)",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'Sora',sans-serif",
  },

  // history toggle button
  historyBtn: {
    flexShrink: 0,
    padding: "6px 14px",
    borderRadius: 10,
    background: "rgba(0,229,255,0.06)",
    border: "1px solid rgba(0,229,255,0.2)",
    color: "#00e5ff",
    fontSize: 11,
    fontWeight: 700,
    fontFamily: "'Space Mono',monospace",
    letterSpacing: "0.06em",
    cursor: "pointer",
  },
  historyBtnActive: {
    background: "rgba(0,229,255,0.14)",
    border: "1px solid rgba(0,229,255,0.4)",
  },
};
